import { Component, computed, inject, Input, model, OnInit, output, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ALERT_TYPE } from 'src/app/constants';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { Business, BusinessBranch, createEmptyLicense, isNullLicense, License, LICENSE_ACTION, LicenseAction, toWithFormattedAddress } from 'src/model/business';
import { BusinessService } from 'src/services/business.service';

@Component({
    selector: 'app-add-update-license',
    templateUrl: './add-update-license.component.html',
    styleUrl: './add-update-license.component.scss',
    standalone: false
})
export class AddUpdateLicenseComponent implements OnInit {
  protected activeModal = inject(NgbActiveModal);
  protected enums = inject(Enums);
  private businessService = inject(BusinessService);
  protected sharedService = inject(SharedService);

  @Input() businessList: Business[];
  @Input() licenseToUpdate: License;
  @Input() businessId: number;
  @Input() lockBusiness = false;
  action = signal<LicenseAction>(LICENSE_ACTION.Add);
  businessBranch = model.required<BusinessBranch>();
  isEdit = signal<boolean>(false);
  license = signal<License>(createEmptyLicense());
  overrideBranchInfo = signal<boolean>(false);
  infoMismatch = computed<boolean>(() => {
    const b = toWithFormattedAddress(this.businessBranch());
    const l = toWithFormattedAddress(this.license());
    return b.FullAddress !== l.FullAddress || b.ContactNo !== l.ContactNo || b.Email !== l.Email;
  });
  passEntry = output<License>();

  minDate = new Date();
  readonly ACTION_LABEL: Record<number, string> = Object.fromEntries(
    Object.entries(LICENSE_ACTION).map(([k, v]) => [v, k])
  );

  ngOnInit(): void {
    this.resetLicense();
  }

  resetLicense() {
    if (this.licenseToUpdate)
      this.license.set(this.sharedService.deepClone(this.licenseToUpdate));
    else {
      this.license.set({
        ...createEmptyLicense(),
        ContactNo: this.businessBranch().ContactNo,
        Email: this.businessBranch().Email,
        Address: this.businessBranch().Address
      });
      // this.license().Address = new Address();
    }
  }

  async addOrUpdateLicense(useBranchInfo: boolean = false) {
    this.license.set({
      ...this.license(),
      BusinessId: this.businessBranch().BusinessId,
      BranchId: this.businessBranch().Id,
      ...(useBranchInfo ? { ContactNo: this.businessBranch().ContactNo, Email: this.businessBranch().Email, Address: this.businessBranch().Address } : {})
    });
    const errors = isNullLicense(this.license());
    if (errors.length > 0) {
      this.sharedService.openAlert(errors.map(e => `- ${e}`).join('\n'), ALERT_TYPE.DANGER);
    } else {
      let t: License;
      switch (this.action()) {
        case LICENSE_ACTION.Update:
          t = await this.businessService.putUpdateBusinessLicense(this.license());
          break;
        case LICENSE_ACTION.Add:
        case LICENSE_ACTION.Renew:
          t = await this.businessService.postAddBusinessLicense(this.license());
          break;
      }
      this.passEntry.emit(t);
      this.activeModal.close(true);
    }
  }

  // #region 'OnChanged'
  onContactNoChanged(value: string) {
    this.license.update(l => ({...l, ContactNo: value}));
  }

  onEmailChanged(value: string) {
    this.license.update(l => ({...l, Email: value}));
  }

  onAddressLine0Changed(value: string) {
    this.license.update(l => ({...l, Address: {...l.Address, Line0: value}}));
  }

  onAddressLine1Changed(value: string) {
    this.license.update(l => ({...l, Address: {...l.Address, Line1: value}}));
  }

  onAddressLine2Changed(value: string) {
    this.license.update(l => ({...l, Address: {...l.Address, Line2: value}}));
  }

  onPostalZoneChanged(value: string) {
    this.license.update(l => ({...l, Address: {...l.Address, PostalZone: value}}));
  }

  onCityChanged(value: string) {
    this.license.update(l => ({...l, Address: {...l.Address, City: value}}));
  }

  onStateChanged(value: string) {
    this.license.update(l => ({...l, Address: {...l.Address, State: value}}));
  }
  // #endregion
}
